import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/apiClient";
import { AdminHeader, AdminPage } from "@/components/admin/AdminUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { VISIBILITY_GROUPS, STAT_ITEMS } from "@/lib/siteNav";

function Field({ label, children }) {
  return <div className="space-y-1.5"><label className="label-eyebrow text-slate-500 block">{label}</label>{children}</div>;
}

export default function SettingsAdmin() {
  const [s, setS] = useState(null);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setStat = (k, v) => setS((p) => ({ ...p, statistics: { ...(p.statistics || {}), [k]: v } }));
  useEffect(() => { api.get("/settings").then(({ data }) => setS(data || {})).catch(() => {}); }, []);
  const save = async () => { try { await api.put("/settings", s); toast.success("Settings saved"); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const hidden = s?.hidden_nav || [];
  const toggle = (id) => setS((p) => {
    const cur = p.hidden_nav || [];
    return { ...p, hidden_nav: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
  });
  const hiddenStats = s?.hidden_stats || [];
  const toggleStat = (id) => setS((p) => {
    const cur = p.hidden_stats || [];
    return { ...p, hidden_stats: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
  });
  if (!s) return <div className="p-8 text-slate-400 animate-pulse">Loading…</div>;

  return (
    <>
      <AdminHeader title="Website Settings" actions={<Button onClick={save} className="bg-npst-navy text-white" data-testid="save-settings-btn">Save Settings</Button>} />
      <AdminPage>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
            <div className="label-eyebrow text-npst-gold">Organisation</div>
            <Field label="Organisation Name"><Input value={s.org_name || ""} onChange={(e) => set("org_name", e.target.value)} /></Field>
            <Field label="Tagline"><Input value={s.tagline || ""} onChange={(e) => set("tagline", e.target.value)} /></Field>
            <Field label="Introduction"><Textarea rows={4} value={s.intro || ""} onChange={(e) => set("intro", e.target.value)} /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Email"><Input value={s.email || ""} onChange={(e) => set("email", e.target.value)} /></Field>
              <Field label="Phone"><Input value={s.phone || ""} onChange={(e) => set("phone", e.target.value)} /></Field>
            </div>
            <Field label="Address"><Textarea rows={2} value={s.address || ""} onChange={(e) => set("address", e.target.value)} /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Registration Number"><Input value={s.registration_number || ""} onChange={(e) => set("registration_number", e.target.value)} /></Field>
              <Field label="Year Established"><Input value={s.established || ""} onChange={(e) => set("established", e.target.value)} /></Field>
            </div>
            <Field label="Office Hours"><Input value={s.office_hours || ""} onChange={(e) => set("office_hours", e.target.value)} /></Field>
          </div>
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-3">
              <div className="label-eyebrow text-npst-gold">Institutional Statistics</div>
              <p className="text-sm text-slate-500 leading-relaxed">All homepage statistics — Public Interest Litigations, Court Orders Archived, Public Representations, Areas of Intervention, Public Documents and Press Releases — are now derived <strong>automatically</strong> from live database content. Manage Areas of Work under the <strong>Areas of Work</strong> section.</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
              <div className="label-eyebrow text-npst-gold">SEO Defaults</div>
              <Field label="SEO Title"><Input value={s.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} /></Field>
              <Field label="Meta Description"><Textarea rows={2} value={s.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} /></Field>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
              <div className="label-eyebrow text-npst-gold">Public Information Disclaimer</div>
              <Field label="Disclaimer Text"><Textarea rows={4} value={s.disclaimer || ""} onChange={(e) => set("disclaimer", e.target.value)} /></Field>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5" data-testid="donation-settings">
              <div className="label-eyebrow text-npst-gold">Donations &amp; Receipts</div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Trust PAN (on receipt)"><Input value={s.trust_pan || ""} onChange={(e) => set("trust_pan", e.target.value.toUpperCase())} /></Field>
                <Field label="Website (on receipt)"><Input value={s.website || ""} onChange={(e) => set("website", e.target.value)} /></Field>
              </div>
              <Field label="Receipt Number Prefix"><Input value={s.receipt_prefix || ""} onChange={(e) => set("receipt_prefix", e.target.value)} placeholder="NPST/DR" /></Field>
              <Field label="Receipt Acknowledgement"><Textarea rows={3} value={s.receipt_acknowledgement || ""} onChange={(e) => set("receipt_acknowledgement", e.target.value)} placeholder="Received with thanks the above-mentioned voluntary donation…" /></Field>
              <Field label="Receipt Footer"><Input value={s.receipt_footer || ""} onChange={(e) => set("receipt_footer", e.target.value)} /></Field>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6 mt-6" data-testid="visibility-manager">
          <div className="label-eyebrow text-npst-gold">Navigation &amp; Visibility</div>
          <p className="text-sm text-slate-500 leading-relaxed mt-1 mb-5">Show or hide any page, policy or text block across the header and footer. Hidden pages are removed from every menu and can no longer be opened directly. Changes go live on the site immediately after you save — no republishing required.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {VISIBILITY_GROUPS.map((group) => (
              <div key={group.title}>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">{group.title}</div>
                <div className="space-y-2.5">
                  {group.items.map((item) => {
                    const visible = !hidden.includes(item.id);
                    return (
                      <div key={item.id} className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-100 last:border-0">
                        <span className={`text-sm ${visible ? "text-npst-ink" : "text-slate-400 line-through"}`}>{item.label}</span>
                        <Switch checked={visible} onCheckedChange={() => toggle(item.id)} data-testid={`visibility-toggle-${item.id}`} />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6 mt-6" data-testid="stats-visibility-manager">
          <div className="label-eyebrow text-npst-gold">Homepage Statistics</div>
          <p className="text-sm text-slate-500 leading-relaxed mt-1 mb-5">Show or hide each statistic in the homepage stats bar. Every visible statistic is counted automatically from live database records. Changes go live immediately after you save.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-2.5">
            {STAT_ITEMS.map((item) => {
              const visible = !hiddenStats.includes(item.id);
              return (
                <div key={item.id} className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-100">
                  <span className={`text-sm ${visible ? "text-npst-ink" : "text-slate-400 line-through"}`}>{item.label}</span>
                  <Switch checked={visible} onCheckedChange={() => toggleStat(item.id)} data-testid={`stat-toggle-${item.id}`} />
                </div>
              );
            })}
          </div>
        </div>
      </AdminPage>
    </>
  );
}
